import { useState, useContext } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ReplayIcon from '@mui/icons-material/Replay';
import { toast } from 'react-toastify';
import { AppContext } from 'store/AppProvider';
import ScaleModal from 'components/ScaleModal/ScaleModal';
import DeleteModal from 'components/DeleteModal/DeleteModal';
import {
  addProgressionScale,
  deleteProgressionScale,
  updateProgressionScale,
} from 'api/ApiChordProgression';
import { ProgressionScalesContext } from 'store/ProgressionScalesProvider';

const ProgressionScalesList = () => {
  const [search, setSearch] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openScaleModal, setOpenScaleModal] = useState(false);
  const { progressionScales } = useContext(AppContext);
  const {
    handleSelectProgressionScale,
    progressionScaleInfo,
    selectedProgressionScaleId,
    setSelectedProgressionScaleId,
  } = useContext(ProgressionScalesContext);
  const { setReloadProgressionScales } = useContext(AppContext);

  const formatScaleLabel = (scale) => `${scale.name}`;

  const canEdit = !!selectedProgressionScaleId;

  const filteredScales = progressionScales.filter((scale) =>
    scale.name.toLowerCase().includes(search)
  );

  const handleDisplayAdd = () => {
    setIsEdit(false);
    setOpenScaleModal(true);
  };

  const handleDisplayDelete = () => {
    if (!canEdit) return;
    setOpenDeleteModal(true);
  };

  const handleDisplayEdit = () => {
    if (!canEdit) return;
    setIsEdit(true);
    setOpenScaleModal(true);
  };

  const handleDeleteScale = async (id) => {
    try {
      await deleteProgressionScale(id);
      toast.success('Progression Scale deleted');
      setSelectedProgressionScaleId('');
      setReloadProgressionScales(true);
    } catch (error) {
      console.error(error);
      toast.error('Cannot delete progression scale');
    }
    setOpenDeleteModal(false);
  };

  const handleAddScale = async (data) => {
    try {
      await addProgressionScale(data);
      toast.success('Scale added');
      setReloadProgressionScales(true);
    } catch (error) {
      console.error(error);
      toast.error('Cannot add scale');
    }
    setOpenScaleModal(false);
  };

  const handleEditScale = async (data) => {
    try {
      await updateProgressionScale(data);
      toast.success('Scale updated');
      setReloadProgressionScales(true);
    } catch (error) {
      console.error(error);
      toast.error('Cannot edit progression scale');
    }
    setOpenScaleModal(false);
  };

  const handleScaleClick = (scale) => {
    handleSelectProgressionScale(scale.id);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <Typography
          sx={{ color: 'text.primary' }}
        >{`Scales (${filteredScales.length})`}</Typography>

        <IconButton onClick={handleDisplayAdd}>
          <AddIcon />
        </IconButton>

        <IconButton onClick={handleDisplayEdit} disabled={!canEdit}>
          <EditIcon />
        </IconButton>

        <IconButton onClick={handleDisplayDelete} disabled={!canEdit}>
          <DeleteIcon />
        </IconButton>
      </Box>

      <TextField
        fullWidth
        placeholder="Search scale"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => {
                  setSearch('');
                }}
                edge="end"
              >
                <ReplayIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      <Stack
        sx={{
          overflowY: 'auto',
          height: '300px',
          flex: '1 1 auto',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {filteredScales.map((scale) => (
          <MenuItem
            key={scale.name}
            value={scale.id}
            sx={{
              backgroundColor: scale.id === selectedProgressionScaleId ? 'green' : '',
            }}
            onClick={() => handleScaleClick(scale)}
          >
            {formatScaleLabel(scale)}
          </MenuItem>
        ))}
      </Stack>

      <ScaleModal
        open={openScaleModal}
        onClose={() => setOpenScaleModal(false)}
        title={isEdit ? 'Edit Scale' : 'Add Scale'}
        submit={isEdit ? handleEditScale : handleAddScale}
        isEdit={isEdit}
        scale={isEdit ? progressionScaleInfo : null}
      />

      <DeleteModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        title="Delete scale"
        submit={handleDeleteScale}
        id={progressionScaleInfo?.id}
        name={progressionScaleInfo?.name}
      />
    </div>
  );
};

export default ProgressionScalesList;
